import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { AuthShell, AuthButton, AuthAlert, authInputClass, authLabelClass } from '../components/AuthShell';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { resetPassword } = useAuthContext();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setMessage('Invalid or missing reset token');
      setIsSuccess(false);
      return;
    }
    if (!formData.password || !formData.confirmPassword) {
      setMessage('Please fill in all fields');
      setIsSuccess(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match');
      setIsSuccess(false);
      return;
    }
    if (formData.password.length < 8) {
      setMessage('Password must be at least 8 characters long');
      setIsSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setMessage(null);
    try {
      const result = await resetPassword({ token, password: formData.password });
      if (result.success) {
        setIsSuccess(true);
        setMessage('Your password has been reset successfully');
      } else {
        setIsSuccess(false);
        setMessage(result.error || 'Failed to reset password. The link may have expired.');
      }
    } catch (error: any) {
      setIsSuccess(false);
      setMessage(error.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const backToSignin = (
    <>
      Remember your password?{' '}
      <Link to="/auth/login" className="font-semibold text-inkblack no-underline hover:text-inkblack">
        Back to sign in
      </Link>
    </>
  );

  // Invalid / missing token
  if (!token) {
    return (
      <>
        <SEO title="Invalid Reset Link" description="Password reset link is invalid or missing" />
        <AuthShell title="Invalid reset link" subtitle="This password reset link is invalid or missing its token." footer={backToSignin}>
          <div className="space-y-5">
            <AuthAlert title="Link can’t be used">
              Please request a fresh password reset link — this one is invalid or has expired.
            </AuthAlert>
            <Link
              to="/auth/forgot-password"
              className="inline-flex w-full items-center justify-center rounded-xl bg-inkblack px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:-translate-y-0.5"
            >
              Request new reset link
            </Link>
          </div>
        </AuthShell>
      </>
    );
  }

  return (
    <>
      <SEO title="Reset Password" description="Create a new password for your account" keywords="reset password, new password" />

      <AuthShell
        title={isSuccess ? 'Password updated' : 'Set a new password'}
        subtitle={isSuccess ? undefined : 'Choose a strong password you don’t use elsewhere.'}
        footer={backToSignin}
      >
        {isSuccess ? (
          <div className="space-y-5">
            <AuthAlert tone="success" title="Password reset successful">{message}</AuthAlert>
            <Link
              to="/auth/login"
              className="inline-flex w-full items-center justify-center rounded-xl bg-inkblack px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:-translate-y-0.5"
            >
              Continue to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {message && !isSuccess && <AuthAlert title="Reset failed" onClose={() => setMessage(null)}>{message}</AuthAlert>}

            <div className="space-y-1.5">
              <label htmlFor="password" className={authLabelClass}>New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.password} onChange={handleInputChange} className={authInputClass} placeholder="Enter new password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-inkblack-60 hover:text-inkblack-60" aria-label="Toggle password">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="confirmPassword" className={authLabelClass}>Confirm new password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                <input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.confirmPassword} onChange={handleInputChange} className={authInputClass} placeholder="Confirm new password" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-inkblack-60 hover:text-inkblack-60" aria-label="Toggle password">
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <ul className="space-y-1.5 rounded-xl bg-receipt-40 px-4 py-3 text-xs text-inkblack-60">
              <li className="flex items-center gap-2">
                <CheckCircle2 className={`h-3.5 w-3.5 ${formData.password.length >= 8 ? 'text-ok-500' : 'text-inkblack-40'}`} />
                At least 8 characters
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className={`h-3.5 w-3.5 ${formData.password && formData.password === formData.confirmPassword ? 'text-ok-500' : 'text-inkblack-40'}`} />
                Both passwords match
              </li>
            </ul>

            <AuthButton type="submit" loading={isSubmitting}>
              {isSubmitting ? 'Resetting password…' : 'Reset password'}
            </AuthButton>
          </form>
        )}
      </AuthShell>
    </>
  );
};

export default ResetPasswordPage;
